import React, { PureComponent } from 'react';
import { Button } from '@ui-propulsion/react-components';
import classNames from 'classnames';

import Modal from '../modal';
import isOwnerEmpty from '../../lib/is-owner-empty';
import Spinner from '../spinner';

const EditLink = (props) => {
  const { className, to, actions, history, id } = props;
  const onClick = (e) => {
    e.preventDefault();
    actions.setPreviousRoute('/patientsolutions/enroll/review');
    history.push(to);
  };

  return <a data-test-id={id} className={className} onClick={onClick}>
    Edit
  </a>;
};

const listOfTerms = [6, 12, 18, 24];

class Review extends PureComponent {

  state = {
    isConfirmOpen: false
  }

  toggleConfirm = () => {
    const isConfirmOpen = !this.state.isConfirmOpen;
    this.setState({ isConfirmOpen });
  }

  confirmReview = () => {
    this.setState({ isConfirmOpen: false });
    const { submitConfirmation } = this.props.actions;
    const { history, actions } = this.props;
    submitConfirmation()
      .then(this.toggleConfirm)
      .then(() => {
        actions.setPreviousRoute('/patientsolutions/enroll/review');
        history.push('/patientsolutions/enroll/agreement');
      })
      .catch(({ response }) => {
        const { status, data } = response;

        if (status && status !== 400) {
          window.location.href = '/patientsolutions/enroll/error';
        }

        if (data && data.redirectTo) {
          window.location.href = data.redirectTo;
        }
      });
  }

  render() {
    const {
      practice,
      onboarding,
      significantController,
      onboarding: {
        no_ownerships_submitted
      }
    } = this.props;

    const {
      isConfirmOpen
    } = this.state;

    const { processingSubmit, hasCompletedReview } = onboarding;
    const owners = this.props.owners.filter((owner) => !isOwnerEmpty(owner)) || [];

    const isYesPath = practice.boSignificantOwnerFlag === 'yes'
      || owners.length > 0;

    const ownerController = this.props.owners.find((owner) => owner.type === 'OWNER_AND_CONTROLLER');

    const controller = ownerController ||
      (significantController.controller || ({ controller: { address: {} }}));

    const renderVal = (cond, template) => cond
      ? template || cond
      : 'N/A';

    const canDisplayTniPni = ({ type }) => type !== 'Fertility' && type !== 'Bariatric';

    const renderFullName = (entity) => {
      if (entity) {
        return renderVal(entity.firstName && entity.lastName,
          `${entity.firstName} ${entity.lastName}`);
      }
      return 'N/A';
    };

    const renderCityState = (address) => {
      if (address && address.city && address.state) {
        return `${address.city}, ${address.state}`;
      }
      return 'N/A';
    };

    const getPaymentPlanMonths = (highestSelectedTerm) => {
      const index = listOfTerms.findIndex((val) => val === parseInt(highestSelectedTerm, 10));
      if (index !== -1) {
        return `${listOfTerms.slice(0, index + 1).join(', ')} months`;
      }
      return 'N/A';
    };

    const renderStreetLine = (address) => {
      if (address && address.aptsuite) {
        return `${address.street}, ${address.aptsuite}`;
      }

      return address && address.street
        ? address.street
        : 'N/A';
    };

    const renderSsn = (entity) => (entity && entity.ssn) ? `XXX-XX-${entity.ssn.slice(5)}` : 'N/A';

    return <section className='header-section review-section'>
      <Modal
        className='review-modal'
        showClose={false}
        isOpen={isConfirmOpen}
        title='Are you sure all your information is correct?'>
        <p data-test-id="modal-last-warning" >This is your last opportunity to make edits. Any errors will delay your registration.</p>
        <Button data-test-id="modal-yes-button" onClick={this.confirmReview} className='confirm-btn confirm-btn-yes'>Yes, I confirm that all the information is correct.</Button>
        <Button data-test-id="modal-no-button" onClick={this.toggleConfirm} className='confirm-btn confirm-btn-no'>No, let me go back and double check.</Button>
      </Modal>
      <h1 className='section-title'>Verify all your information is correct</h1>
      <p>Your registration is almost complete! Please double check that all the information you’ve provided is correct before the last step, signing your agreement. This is your last chance to edit your information.</p>
      <div className='info-box'>
        <div data-test-id="verification-warning" className='info-box-symbol info-box-warning' />
        <div className='info-box-body'>
          Any typos or incorrect information will delay your registration.
        </div>
      </div>

      <div className='onboarding-sections'>
        <div className='onboarding-section'>
          <div className='onboarding-section-body'>
            <div className='onboarding-section-title'>
              <h2 className='primary-contact-info-sm'>Primary Contact Info</h2>
              <h2 className='primary-contact-info-lg'>Primary Contact Information</h2>
            </div>
            <EditLink
              {...this.props}
              id="primary-contact-edit-button"
              className='section-edit'
              to='/patientsolutions/enroll/contact-information?status=review'
            />
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Name</div>
            <div>{renderVal(practice && practice.contactFirstname && practice.contactLastname,
              `${practice.contactFirstname} ${practice.contactLastname}`)}
            </div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Login Email</div>
            <div>{renderVal(practice.email)}</div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Phone</div>
            <div>{renderVal(practice.phone)}</div>
          </div>
        </div>

        <div className='onboarding-section'>
          <div className='onboarding-section-body'>
            <div className='onboarding-section-title'><h2>Doctor Information</h2></div>
            <EditLink
              {...this.props}
              id="doctor-information-edit-button"
              className='section-edit'
              to='/patientsolutions/enroll/contact-information?status=review#doctor-info'
            />
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Name</div>
            <div>
              {
                renderVal(practice && practice.doctorFirstname && practice.doctorLastname,
                  `${practice.doctorFirstname} ${practice.doctorLastname}`)
              }
            </div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Email</div>
            <div>{renderVal(practice.doctorEmail)}</div>
          </div>
        </div>

        <div className='onboarding-section'>
          <div className='onboarding-section-body'>
            <div className='onboarding-section-title'><h2>Practice Information</h2></div>
            <EditLink
              {...this.props}
              id="practice-information-edit-button"
              className='section-edit'
              to='/patientsolutions/enroll/practice-information?status=review'
            />
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Name</div>
            <div>{renderVal(practice.name)}</div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Practice Type</div>
            <div>{renderVal(practice.type)}</div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Business Type</div>
            <div>{renderVal(practice.businessType)}</div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Address</div>
            <div>{renderStreetLine(practice)}</div>
            <div>{renderCityState(practice)}</div>
            <div>{renderVal(practice.zip)}</div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Legal Business Name</div>
            <div>{practice.legalName}</div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Tax Identification Number (TIN)</div>
            <div>{renderVal(practice.businessTIN)}</div>
          </div>
        </div>

        <div className='onboarding-section payment-section'>
          <div className='onboarding-section-body'>
            <div className='onboarding-section-title'><h2>Payment Options</h2></div>
            { canDisplayTniPni(practice) && 
            <EditLink
                {...this.props}
                id="payment-options-edit-button"
                className='section-edit'
                to='/patientsolutions/enroll/payment-plans?status=review'
              />
            }
          </div>
          <div className='section-data'>
            {
              canDisplayTniPni(practice) && <div className='pni-terms'>
                <div className='section-data-name'>Promotional No-Interest Plans</div>
                <div>{getPaymentPlanMonths(practice.maxPNITerm)}</div>
              </div>
            }
            <div className='section-data-name section-note prp-fpr-msg'>
             {
               canDisplayTniPni(practice)
                 ? <div>Fixed and Promotional Rate Plans <br /> Automatically Included</div>
                 : <div>Fixed Rate Plan <br /> Automatically Included</div>
             }
            </div>
          </div>
        </div>

        <div id='onboarding-section-owner' className='onboarding-section'>
          <div className='onboarding-section-body'>
            <div className='onboarding-section-title'><h2>Ownership Info</h2></div>
              <EditLink
                {...this.props}
                id="ownership-info-edit-button"
                className='section-edit'
                to={
                  no_ownerships_submitted
                    ? '/patientsolutions/enroll/ownership?status=review'
                    : '/patientsolutions/enroll/ownership/add-owners?status=review'
                }
              />
          </div>
          {
            isYesPath && owners.length > 0
              ? owners
                  .map((owner, idx) => <div className='section-data-owner' key={owner.firstName + owner.lastName + idx + 'review'}>
                    <div className='section-data'>
                      <div className='section-data-name'>{`Owner #${idx + 1}`}</div>
                      <div>{renderFullName(owner)}</div>
                    </div>
                    <div className='section-data'>
                      <div className='section-data-name'>Ownership Share</div>
                      <div>{renderVal(parseInt(owner.share, 10) >= 0, `${owner.share}%`)}</div>
                    </div>
                    <div className='section-data'>
                      <div className='section-data-name'>Date of Birth</div>
                      <div>{renderVal(owner.dob)}</div>
                    </div>
                    <div className='section-data'>
                      <div className='section-data-name'>Social Security Number</div>
                      <div>{renderSsn(owner)}</div>
                    </div>
                    <div className='section-data'>
                      <div className='section-data-name'>Residential Address</div>
                      <div>{renderStreetLine(owner.address)}</div>
                      <div>{renderCityState(owner.address)}</div>
                      <div>{renderVal(owner && owner.address && owner.address.zip)}</div>
                    </div>
                  </div>)
            : <div className='section-data'>N/A<br/></div>
          }
        </div>

        <div id='onboarding-section-management-info' className='onboarding-section'>
          <div className='onboarding-section-body'>
            <div className='onboarding-section-title'><h2>Management Info</h2></div>
            <EditLink
              {...this.props}
              id="managment-info-edit-button"
              className='section-edit'
              to='/patientsolutions/enroll/ownership/significant-owner?status=review'
            />
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Who has significant responsibility <br /> to manage the practice?</div>
            <div id='practice_controller_name'>{renderFullName(controller)}</div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Date of Birth</div>
            <div>{renderVal(controller.dob)}</div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Social Security Number</div>
            <div>{renderSsn(controller)}</div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Residential Address</div>
            <div>{renderStreetLine(controller.address)}</div>
            <div>{renderCityState(controller.address)}</div>
            <div>{renderVal(controller && controller.address && controller.address.zip)}</div>
          </div>
        </div>

        <div className='onboarding-section'>
          <div className='onboarding-section-body'>
            <div className='onboarding-section-title'><h2>Bank Information</h2></div>
            <EditLink
              {...this.props}
              id="bank-information-edit-button"
              className='section-edit'
              to='/patientsolutions/enroll/ach-information?status=review'
            />
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Account Holder Name</div>
            <div>{renderVal(practice.achAccountName)}</div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Routing Number</div>
            <div>{renderVal(practice.achRoutingNumber)}</div>
          </div>
          <div className='section-data'>
            <div className='section-data-name'>Account Number</div>
            <div>{renderVal(practice.achAccountNumber)}</div>
          </div>
        </div>
      </div>

      <div className='action-area'>
        <Button
          data-test-id="verify-button"
          className={classNames('button-submit', {
            disabled: hasCompletedReview
          })}
          disabled={hasCompletedReview}
          onClick={this.toggleConfirm}
          aria-label='practice submit button'>
          <span className='button-submit-text'>Verify</span>
        </Button>
      </div>

      { processingSubmit && <Spinner processingSubmit={processingSubmit}/> }

    </section>;
  }
}

export default Review;

